import React, { useCallback } from "react";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import { useOpenSidePanel } from "../SidePanel";
import { UserFirstLastName } from "UserAdmin/components/UserFirstLastName";
import { EmailInput } from "components";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { useCustomQuery } from "utilities/hookutils";
import GuestlistListsDropdown from "components/dropdowns/reactselect/GuestlistAccessTypeDropdown";
import { useQueryClient } from "@tanstack/react-query";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredTextAreaInput from "components/ValidatedFields/RequiredTextAreaInput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";

interface NewGuestFormState {
  firstName: string;
  lastName: string;
  bringsGuests: number;
  list: server.dto.GuestlistList;
  email: string;
  mobile: string;
  passport: string;
  notes: string;
}

interface Props {
  guestlistId: number;
  onCreated: () => void;
}

const NewGuestSidePanel = (props: Props) => {
  const afterCreate = "closeSidebar";

  const queryClient = useQueryClient();

  const guestlistConfigApi = useCustomQuery({
    queryFn: () => window.CoreApi.Guestlist.GetSidePanelData(props.guestlistId).then(res => res.data),
    queryKey: ["guestlistConfiguration", props.guestlistId],
  });

  const bringsGuestsEnabled = guestlistConfigApi.data?.data?.configuration.multipleGuestsPerName;
  const emailEnabled = guestlistConfigApi.data?.data?.configuration.guestEmailAddressFormElementBehavior === "visibleMandatory";
  const mobileEnabled = guestlistConfigApi.data?.data?.configuration.guestMobileNumberFormElementBehavior === "visibleMandatory";
  const passportEnabled = guestlistConfigApi.data?.data?.configuration.guestPassportNumberFormElementBehavior === "visibleMandatory";
  const notesEnabled = guestlistConfigApi.data?.data?.configuration.guestNotesFormElementBehavior !== "notVisible";
  const notesRequired = guestlistConfigApi.data?.data?.configuration.guestNotesFormElementBehavior === "visibleMandatory";
  const lists = guestlistConfigApi.data?.data?.lists ?? [];

  const [formState, setFormState] = React.useState<NewGuestFormState>({
    firstName: "",
    lastName: "",
    bringsGuests: 0,
    list: null,
    email: "",
    mobile: "",
    passport: "",
    notes: "",
  });

  const onChange = (newData: Partial<NewGuestFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const submit = () => {
    return window.CoreApi.Guestlist.CreateGuest({
      guestlistId: props.guestlistId,
      bringsGuests: formState.bringsGuests,
      name: {
        first: formState.firstName,
        last: formState.lastName,
      },
      guestlistAccessTypeId: formState.list.guestlistAccessTypeId,
      emailAddress: formState.email,
      mobileNumber: formState.mobile,
      passportNumber: formState.passport,
      notes: formState.notes,
    });
  }

  const onSuccess = async () => {
    props.onCreated();
  }

  return <SidePanelNewSectionTemplate
    onSave={() => {
      onSuccess();
      queryClient.invalidateQueries(["guestlist"]);
    }}
    afterCreate={afterCreate}
    title={RESX.Guestlist.Guest}
    id="new-guest-side-panel"
    trigger={undefined}
    submit={submit}
    fields={[{
      key: "name",
      label: RESX.GeneralLabels.Name,
      renderEditNew: () => <UserFirstLastName
        firstName={formState.firstName}
        lastName={formState.lastName}
        onChange={(firstName: string, lastName: string) => {
          onChange({ firstName, lastName });
        }}
      />
    }, {
      key: "bringsGuests",
      label: RESX.Guestlist.BringsGuestsLbl,
      hidden: !bringsGuestsEnabled,
      renderEditNew: () => <OptionalNumberInput
        minValue={0}
        maxValue={999}
        canBeNegative={false}
        canBeEmpty
        width={75}
        value={formState.bringsGuests}
        onChange={(v: number) => onChange({ bringsGuests: v })}
        onEnterPressed={(v: number) => onChange({ bringsGuests: v })}
        decimalPoints={0}
      />
    }, {
      key: "accessType",
      label: RESX.Guestlist.List,
      renderEditNew: () => <GuestlistListsDropdown
        options={lists}
        value={formState.list}
        onChange={(list) => { onChange({ list: list }); }}
      />
    }, {
      key: "email",
      label: RESX.Email.EmailAddress,
      hidden: !emailEnabled,
      renderEditNew: () => <EmailInput
        value={formState.email}
        maxLength={serverReference.constants.mailAddress.maxEmailLength}
        onChange={(email: string) => { onChange({ email }) }}
        validatorKey="email"
        emailCanBeBlank={false}
      />
    }, {
      key: "mobile",
      label: RESX.Contact.Mobile,
      hidden: !mobileEnabled,
      renderEditNew: () => <RequiredTextInput
        validatorKey="mobile"
        maxLength={serverReference.constants.guestlist.maxMobileNumberLength}
        value={formState.mobile}
        onChange={(mobile) => { onChange({ mobile }); }}
      />
    }, {
      key: "passport",
      label: RESX.Passport.PassportNumber,
      hidden: !passportEnabled,
      renderEditNew: () => <RequiredTextInput
        validatorKey="passport"
        maxLength={serverReference.constants.guestlist.maxPassportNumberLength}
        value={formState.passport}
        onChange={(passport) => { onChange({ passport }); }}
      />
    }, {
      key: "notes",
      label: RESX.Note.Notes,
      hidden: !notesEnabled,
      renderEditNew: () => notesRequired ? <RequiredTextAreaInput
        validatorKey="notes"
        rows={3}
        maxLength={serverReference.constants.guestlist.maxNotesLength}
        value={formState.notes}
        onChange={(notes) => { onChange({ notes }); }}
      /> : <OptionalTextAreaInput
        rows={3}
        maxLength={serverReference.constants.guestlist.maxNotesLength}
        value={formState.notes}
        onChange={(notes) => { onChange({ notes }); }}
      />
    }]}
  />
}

export const useOpenNewGuestSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewGuestSidePanel = useCallback((guestlistId: number, onCreated: () => void) => {
    openSidePanel(
      <NewGuestSidePanel guestlistId={guestlistId} onCreated={onCreated} />,
      "new-guest-side-panel",
      "medium"
    );
  }, [openSidePanel]);

  return openNewGuestSidePanel;
}